import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { currentCompanyDateFormatSelector, shouldHighlightAmountSelector } from '@/domain/companies/companiesSelector';
import moment from 'moment';

import { SYMBOL_FOR_EMPTY_CELL, valueFormatterNumber, valueFormatterAmount } from '@/components/AgGrid/helpers';

const CELL_STYLES = {
  display: 'flex',
  alignItems: 'center',
  padding: '0px',
  maxWidth: '100%',
};

const useAgGridColumnTypes = () => {
  const companyDateFormat = useSelector(currentCompanyDateFormatSelector);
  const shouldHighlightAmount = useSelector(shouldHighlightAmountSelector);
  const dateFormat = companyDateFormat || 'DD/MM/YYYY';

  // apply filter params in addFilters method - here only if you need some data from other places (redux etc)
  return useMemo(
    () => ({
      text: {},
      date: {
        filterParams: {
          format: dateFormat,
        },
        valueFormatter: ({ value }) => (value ? moment(value).format(dateFormat) : SYMBOL_FOR_EMPTY_CELL),
      },
      number: {
        cellStyle: { textAlign: 'right' },
        valueFormatter: valueFormatterNumber,
      },
      string: {},
      boolean: { cellRenderer: 'agCheckboxCellRenderer' },
      actions: {
        width: 0,
      },
      integer: {
        valueFormatter: ({ value }) => (typeof value === 'number' ? parseInt(value, 10) : SYMBOL_FOR_EMPTY_CELL),
      },
      select: {},
      extraSelect: {
        cellRenderer: 'alertCellRenderer',
        cellRendererParams: {
          needContainerForDetectOverflow: true,
        },
        valueFormatter: ({ value }) =>
          Array.isArray(value) && !!value.length ? value.join(';') : SYMBOL_FOR_EMPTY_CELL,
      },
      static_select: {},
      amount: {
        cellRenderer: 'amountCellRenderer',
        cellClass: 'amount',
        cellStyle: { textAlign: 'right' },
        valueFormatter: valueFormatterAmount(shouldHighlightAmount),
      },
      linkedString: {
        cellRenderer: 'linksCellRenderer',
        valueFormatter: ({ value }) =>
          Array.isArray(value) && value.length > 0
            ? value.map((item) => item.referenceNumber).join(', ')
            : SYMBOL_FOR_EMPTY_CELL,
      },
      colorString: {
        cellRenderer: 'colorStringCellRenderer',
        valueFormatter: ({ value: data }) => (data?.value ? data?.value : SYMBOL_FOR_EMPTY_CELL),
      },
      approvalsSet: {
        cellRenderer: 'approvalsCellRenderer',
        cellStyle: {
          ...CELL_STYLES,
        },
        maxWidth: 1000,
        minWidth: 100,
      },
    }),
    [shouldHighlightAmount, dateFormat],
  );
};

export default useAgGridColumnTypes;
